export const SOURCES = [
  'github',
  'pool',
  'linkedin',
  'instagram',
  'djinni',
  'dou',
  'workua',
  'robotaua',
  'telegram',
] as const;

export const SOURCE_LABELS: Record<(typeof SOURCES)[number], string> = {
  github: 'GitHub',
  pool: 'Talent Pool',
  linkedin: 'LinkedIn',
  instagram: 'Instagram',
  djinni: 'Djinni',
  dou: 'DOU',
  workua: 'Work.ua',
  robotaua: 'Robota.ua',
  telegram: 'Telegram',
};

/**
 * Sources that only ever return synthetic (demo) data. Candidates from these
 * carry a visible "Demo" badge and are excluded from real counts. (LinkedIn is
 * now an X-ray Assist source — see SOURCE_KIND — so only Telegram remains demo.)
 */
export const DEMO_SOURCES = ['telegram'] as const;

/** True when a source only ever yields synthetic/demo candidates. */
export function isDemoSource(id: string): boolean {
  return (DEMO_SOURCES as readonly string[]).includes(id);
}

/**
 * How each source's data is obtained, for honest labeling (badges match reality):
 * - `live-official` first-class official API (GitHub)
 * - `assist`        X-ray via an official search API — link-out + manual import,
 *                   never fetches the destination platform page
 * - `live-own`      Sourcr's own consent-based candidate pool
 * - `public-beta`   best-effort public-page scrape (interim, pre-assist)
 * - `demo`          synthetic data only
 */
export type SourceKind = 'live-official' | 'assist' | 'live-own' | 'public-beta' | 'demo';

export const SOURCE_KIND: Record<(typeof SOURCES)[number], SourceKind> = {
  github: 'live-official',
  // Sourcr's own consent-based pool — real, first-class candidates.
  pool: 'live-own',
  // LinkedIn, Instagram + UA boards surface public profiles via X-ray assist (link-out only).
  djinni: 'assist',
  dou: 'assist',
  workua: 'assist',
  robotaua: 'assist',
  linkedin: 'assist',
  instagram: 'assist',
  telegram: 'demo',
};

/** True when a source surfaces public profiles via X-ray assist (link-out only). */
export function isAssistSource(id: string): boolean {
  return SOURCE_KIND[id as (typeof SOURCES)[number]] === 'assist';
}

/**
 * Honestly-active sources for marketing/empty-state copy (single source of
 * truth — TASK-39). GitHub is a real API; boards + LinkedIn are public X-ray
 * link-out (no scraping); pool is consent-based. No "7 platforms", no Djinni
 * full-fetch, no cookie scraping — we never advertise what we can't legally do.
 */
export const ACTIVE_SOURCES = [
  { key: 'github', kind: 'api', label: 'GitHub' },
  { key: 'linkedin', kind: 'xray', label: 'LinkedIn' },
  { key: 'workua', kind: 'xray', label: 'Work.ua' },
  { key: 'robotaua', kind: 'xray', label: 'Robota.ua' },
  { key: 'pool', kind: 'optin', label: 'Talent Pool' },
] as const;

/**
 * Static UA+EN synonym seeds for common non-dev roles (TASK-C). Used to build
 * bilingual X-ray OR-queries so recall doesn't depend on a (rate-limited) LLM —
 * "Customer Support" still matches Ukrainian résumés. Matched by substring on the
 * lowercased role. The LLM expansion (when available) adds more on top.
 */
const ROLE_SYNONYMS: Array<{ match: RegExp; terms: string[] }> = [
  { match: /customer support|support (specialist|agent|rep)|tech(nical)? support|підтримк|сапорт/i, terms: ['customer support', 'support specialist', 'спеціаліст підтримки', 'оператор підтримки', 'технічна підтримка', 'підтримка клієнтів'] },
  { match: /translat|interpret|перекладач/i, terms: ['translator', 'interpreter', 'перекладач'] },
  { match: /account(ant|ing)|бухгалтер/i, terms: ['accountant', 'бухгалтер', 'головний бухгалтер'] },
  { match: /\bsales\b|продаж/i, terms: ['sales manager', 'менеджер з продажу', 'sales representative', 'фахівець з продажу'] },
  { match: /recruit|talent acquisition|рекрутер|ейчар|\bhr\b/i, terms: ['recruiter', 'рекрутер', 'talent acquisition', 'HR'] },
  { match: /marketing|marketer|маркетолог|\bsmm\b/i, terms: ['marketing', 'маркетолог', 'marketer'] },
  { match: /designer|дизайнер|\bux\b|\bui\b/i, terms: ['designer', 'дизайнер', 'UI/UX'] },
  { match: /project manager|проджект|керівник проєкту|менеджер проєкту/i, terms: ['project manager', 'проджект менеджер', 'керівник проєкту'] },
  { match: /office manager|офіс.менеджер|адміністратор/i, terms: ['office manager', 'офіс-менеджер', 'адміністратор'] },
  { match: /копірайтер|copywriter|content writer/i, terms: ['copywriter', 'копірайтер', 'контент-менеджер'] },
];

/** Bilingual synonym seeds for a free-text role (empty when no static match). */
export function roleSynonyms(role: string | undefined | null): string[] {
  const r = (role ?? '').toLowerCase();
  for (const entry of ROLE_SYNONYMS) if (entry.match.test(r)) return entry.terms;
  return [];
}

export const DEFAULT_LANGUAGE = 'en' as const;

export const GRADE_THRESHOLDS = {
  A_PLUS: 90,
  A: 80,
  B_PLUS: 70,
  B: 60,
} as const;

/** Grade label from 0–100 score (matches prototype logic) */
export function gradeOf(pct: number): string {
  if (pct >= GRADE_THRESHOLDS.A_PLUS) return 'A+';
  if (pct >= GRADE_THRESHOLDS.A) return 'A';
  if (pct >= GRADE_THRESHOLDS.B_PLUS) return 'B+';
  if (pct >= GRADE_THRESHOLDS.B) return 'B';
  return 'C';
}

/** Grade color CSS variable name (dark theme) */
export function gradeColor(pct: number): string {
  if (pct >= GRADE_THRESHOLDS.A) return 'var(--success)';
  if (pct >= GRADE_THRESHOLDS.B) return 'var(--warning)';
  return 'var(--danger)';
}

export const API_BASE_URL = '/api';

export const MAX_CANDIDATES_PER_SEARCH = 50;
export const MAX_CONCURRENT_SOURCES = 8;
